import { useEffect, useReducer, useRef, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { OosConfig } from '@/types/cgt'
import type { OosAlertView } from '@/api/formsApi'
import {
  OOS_COMMENT_REQUIRED_MESSAGE,
  OOS_SEVERITY_LABELS,
  describeOosRange,
  describeOosRule,
  evaluateOos,
  formatOosValue,
  matchingOosRule,
  oosFieldType,
  sameOosValue,
  type OosResult,
} from '@/lib/oos'
import { useInvalidateOosAlerts, useOosAlerts, useReviewOosAlert, useSetOosAlertComment } from '@/hooks/useOosAlerts'
import { usePermission } from '@/lib/permissions'

const errorText = (e: unknown) => (e instanceof Error && e.message ? e.message : 'The value could not be saved.')

interface Options {
  /** The specification that grades this value. Undefined makes the guard inert. */
  spec: OosConfig | undefined
  fieldType: string
  /** The field id, or the cell key for a table cell. Alerts carry the same id. */
  fieldId: string
  /** The value the server holds now. */
  savedValue: unknown
  submissionId: string
  preview: boolean
  locked: boolean
  /** Saves and throws on failure, so a refused save keeps the prompt open. */
  onSave: (value: unknown, comment?: string) => Promise<void>
  /**
   * Called when the author cancels the comment prompt. Controls that show the
   * saved value (dropdown, checkbox) pass this to go back to it. Without it the
   * value stays where it was typed, unsaved, next to the "comment required"
   * message.
   */
  onCancel?: () => void
  /** A small dot instead of the severity badge, for table cells. */
  compactFlag?: boolean
  /**
   * Whether the open alert's comment may be added or changed. Defaults to
   * `!locked`. A calculated field is always locked for typing but still takes
   * a comment while the form is open.
   */
  commentable?: boolean
}

/**
 * Out-of-specification behaviour for one value (z8nrz7eq4e, z8nrz7eq4h): asks
 * for a comment before saving a value whose band requires one, shows the flag
 * for the saved value, and lets an alerted group member review the alert from
 * the flag. The caller owns the input and routes its saves through `guardSave`.
 */
const TEXT_TYPES = new Set(['text', 'textarea'])

export function useOosGuard({
  spec,
  fieldType,
  fieldId,
  savedValue,
  submissionId,
  preview,
  locked,
  onSave,
  onCancel,
  compactFlag = false,
  commentable,
}: Options) {
  const [saving, setSaving] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [pending, setPending] = useState<{ value: unknown } | null>(null)
  const [comment, setComment] = useState('')
  const savedValueRef = useRef<unknown>(savedValue)
  const savingRef = useRef(false)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const alertsQuery = useOosAlerts(submissionId, !preview && !!spec)
  const invalidateAlerts = useInvalidateOosAlerts()

  // Follows the saved value when it changes from outside (another save, a
  // reload), but not mid-save.
  // The ref is read during render, so a change from outside re-renders once.
  const [, rerender] = useReducer((n: number) => n + 1, 0)
  useEffect(() => {
    if (savingRef.current || savedValueRef.current === savedValue) return
    savedValueRef.current = savedValue
    rerender()
  }, [savedValue])

  useEffect(() => () => {
    if (savedTimer.current) clearTimeout(savedTimer.current)
  }, [])

  const save = async (value: unknown, withComment?: string) => {
    savingRef.current = true
    setSaving(true)
    setSaveError(null)
    try {
      await onSave(value, withComment)
      savedValueRef.current = value
      setPending(null)
      setComment('')
      setShowSaved(true)
      if (savedTimer.current) clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(() => setShowSaved(false), 2000)
      if (!preview) invalidateAlerts()
    } catch (e) {
      setSaveError(errorText(e))
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  /** Saves `value`, asking for a comment first when its band requires one. */
  const guardSave = (value: unknown) => {
    // Text keeps every character a person typed: an edit that only adds a
    // space is still saved, even though it grades the same.
    const unchanged = TEXT_TYPES.has(fieldType)
      ? String(value ?? '') === String(savedValueRef.current ?? '')
      : sameOosValue(fieldType, value, savedValueRef.current)
    if (locked || unchanged) return
    if (evaluateOos(spec, value, fieldType)?.commentRequired) {
      setSaveError(null)
      setPending({ value })
      return
    }
    void save(value)
  }

  const cancelComment = () => {
    setPending(null)
    if (onCancel) onCancel()
    // The value stays in the box, unsaved, until a comment is given.
    else setSaveError(OOS_COMMENT_REQUIRED_MESSAGE)
  }

  const savedFlag = evaluateOos(spec, savedValueRef.current, fieldType)
  const alert = (alertsQuery.data ?? []).find(
    a => a.fieldId === fieldId && a.status !== 'auto_closed' && sameOosValue(fieldType, a.value, savedValueRef.current),
  )
  const pendingFlag = pending ? evaluateOos(spec, pending.value, fieldType) : null

  const flag: ReactNode =
    spec && savedFlag ? (
      <OosFlag
        spec={spec}
        fieldType={fieldType}
        value={savedValueRef.current}
        flag={savedFlag}
        alert={alert}
        submissionId={submissionId}
        compact={compactFlag}
        commentable={!preview && (commentable ?? !locked)}
      />
    ) : null

  const dialog: ReactNode = spec ? (
    <Dialog
      open={pending !== null}
      // Closing mid-save would report "comment required" over a save that
      // is about to succeed.
      onOpenChange={open => (open || saving ? undefined : cancelComment())}
    >
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Comment required</DialogTitle>
          <DialogDescription>
            {pending && pendingFlag
              ? `${formatOosValue(fieldType, pending.value)} is out of specification (${OOS_SEVERITY_LABELS[pendingFlag.severity]}). Add a comment to save it.`
              : OOS_COMMENT_REQUIRED_MESSAGE}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-1.5'>
          <Label htmlFor={`oos-comment-${fieldId}`}>Comment</Label>
          <Textarea
            id={`oos-comment-${fieldId}`}
            value={comment}
            rows={3}
            maxLength={2000}
            onChange={e => setComment(e.target.value)}
          />
          {saveError && <p className='text-xs text-destructive'>{saveError}</p>}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={cancelComment} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => pending && void save(pending.value, comment)} disabled={saving || !comment.trim()}>
            {saving && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null

  return {
    guardSave,
    saving,
    showSaved,
    saveError,
    clearError: () => setSaveError(null),
    /** True while a save is in flight; callers skip re-syncing their input then. */
    savingRef,
    /** The value the guard counts as saved. */
    savedValue: savedValueRef.current,
    /** The value waiting on a comment, while the prompt is open. */
    pending,
    /** The saved value's colour, for the input border. */
    borderColor: savedFlag?.color,
    flag,
    dialog,
  }
}

export type OosGuard = ReturnType<typeof useOosGuard>

/** What a control without a specification gets: saves as before, shows nothing. */
export const INERT_OOS_GUARD: OosGuard = {
  guardSave: () => {},
  saving: false,
  showSaved: false,
  saveError: null,
  clearError: () => {},
  savingRef: { current: false },
  savedValue: undefined,
  pending: null,
  borderColor: undefined,
  flag: null,
  dialog: null,
}

/**
 * Runs `useOosGuard` for a control that has a specification and hands the
 * guard to `children`. A control without one renders with INERT_OOS_GUARD and
 * never mounts this, so it never reads the alert list, the tenant or the
 * query client.
 */
export function OosGuardScope({ children, ...options }: Options & { children: (guard: OosGuard) => ReactNode }) {
  const guard = useOosGuard(options)
  return <>{children(guard)}</>
}

function OosFlag({
  spec,
  fieldType,
  value,
  flag,
  alert,
  submissionId,
  compact,
  commentable,
}: {
  spec: OosConfig
  fieldType: string
  value: unknown
  flag: OosResult
  alert: OosAlertView | undefined
  submissionId: string
  compact: boolean
  commentable: boolean
}) {
  const [actionNote, setActionNote] = useState('')
  const [showActionNote, setShowActionNote] = useState(false)
  const [commentDraft, setCommentDraft] = useState<string | null>(null)
  const review = useReviewOosAlert(submissionId)
  const setAlertComment = useSetOosAlertComment(submissionId)
  const { can } = usePermission()
  const canComment = commentable && alert?.status === 'open' && can('submissions.edit')
  const saveComment = () => {
    if (!alert || commentDraft === null) return
    setAlertComment.mutate(
      { alertId: alert.id, comment: commentDraft },
      { onSuccess: () => setCommentDraft(null) },
    )
  }
  const label = OOS_SEVERITY_LABELS[flag.severity]
  const status = alert?.status === 'open' ? 'Awaiting review' : alert?.status === 'reviewed' ? 'Reviewed' : null
  const ariaLabel = `Out of specification: ${label}${status ? `, ${status.toLowerCase()}` : ''}`

  let reason: string
  if (oosFieldType(fieldType) === 'number') {
    const range = describeOosRange(spec)
    reason = range ? ` In spec: ${range}.` : ''
  } else {
    const band = matchingOosRule(spec, value)
    reason = band ? ` Flagged when the value ${describeOosRule(fieldType, band.when)}.` : ''
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        {compact ? (
          <button
            type='button'
            className='inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full'
            aria-label={ariaLabel}
            title={ariaLabel}
          >
            <span className='h-2.5 w-2.5 rounded-full' style={{ backgroundColor: flag.color }} />
          </button>
        ) : (
          <button
            type='button'
            className='inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium'
            style={{ borderColor: flag.color, color: flag.color }}
            aria-label={ariaLabel}
          >
            <span className='h-2 w-2 rounded-full' style={{ backgroundColor: flag.color }} />
            {label}
            {status && <span className='font-normal text-muted-foreground'>· {status}</span>}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className='w-72 space-y-2 text-sm' align='end'>
        <div>
          <p className='font-medium'>Out of specification ({label})</p>
          <p className='text-xs text-muted-foreground'>
            Value {formatOosValue(fieldType, value)}.{reason}
          </p>
          {compact && status && <p className='text-xs text-muted-foreground'>{status}</p>}
        </div>
        {alert && (
          <div className='space-y-1 text-xs'>
            <p>Entered by {alert.raisedBy}</p>
            {commentDraft === null ? (
              <>
                {alert.entryComment && <p className='text-muted-foreground'>Comment: {alert.entryComment}</p>}
                {canComment && (
                  <Button
                    size='sm'
                    variant='link'
                    className='h-auto p-0 text-xs'
                    onClick={() => setCommentDraft(alert.entryComment ?? '')}
                  >
                    {alert.entryComment ? 'Edit comment' : 'Add comment'}
                  </Button>
                )}
              </>
            ) : (
              <div className='space-y-2'>
                <Label htmlFor={`oos-entry-comment-${alert.id}`} className='text-xs'>
                  Comment
                </Label>
                <Textarea
                  id={`oos-entry-comment-${alert.id}`}
                  rows={3}
                  maxLength={2000}
                  value={commentDraft}
                  onChange={e => setCommentDraft(e.target.value)}
                />
                <div className='flex justify-end gap-2'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => setCommentDraft(null)}
                    disabled={setAlertComment.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    size='sm'
                    onClick={saveComment}
                    disabled={setAlertComment.isPending || !commentDraft.trim()}
                  >
                    {setAlertComment.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
                    Save
                  </Button>
                </div>
              </div>
            )}
            {alert.status === 'reviewed' && (
              <p>
                {alert.reviewAction === 'action_taken' ? 'Action taken' : 'Acknowledged'} by {alert.reviewedBy}
                {alert.reviewComment ? `: ${alert.reviewComment}` : ''}
              </p>
            )}
          </div>
        )}
        {alert?.canReview && (
          <div className='space-y-2 border-t pt-2'>
            {showActionNote ? (
              <>
                <Label htmlFor={`oos-action-${alert.id}`} className='text-xs'>
                  Action taken
                </Label>
                <Textarea
                  id={`oos-action-${alert.id}`}
                  rows={3}
                  maxLength={2000}
                  value={actionNote}
                  onChange={e => setActionNote(e.target.value)}
                />
                <div className='flex justify-end gap-2'>
                  <Button size='sm' variant='outline' onClick={() => setShowActionNote(false)} disabled={review.isPending}>
                    Back
                  </Button>
                  <Button
                    size='sm'
                    disabled={review.isPending || !actionNote.trim()}
                    onClick={() => review.mutate({ alertId: alert.id, action: 'action_taken', comment: actionNote })}
                  >
                    Save
                  </Button>
                </div>
              </>
            ) : (
              <div className='flex justify-end gap-2'>
                <Button
                  size='sm'
                  variant='outline'
                  disabled={review.isPending}
                  onClick={() => review.mutate({ alertId: alert.id, action: 'acknowledge' })}
                >
                  Acknowledge
                </Button>
                <Button size='sm' disabled={review.isPending} onClick={() => setShowActionNote(true)}>
                  Action taken
                </Button>
              </div>
            )}
          </div>
        )}
        {review.error && <p className='text-xs text-destructive'>{errorText(review.error)}</p>}
        {setAlertComment.error && <p className='text-xs text-destructive'>{errorText(setAlertComment.error)}</p>}
      </PopoverContent>
    </Popover>
  )
}
